import { zodResolver } from '@hookform/resolvers/zod';
import { tenantProfileSchema, type TenantProfileInput } from '@serviceportal/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Globe, ImageUp, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Field, PageHeader } from '@/components/ui/form-field';
import { Badge, Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle, Input, Select, Skeleton, Switch } from '@/components/ui/primitives';
import { api, errorMessage } from '@/lib/api';
import { ME_KEY } from '@/lib/auth';
import { logoUrl } from '@/lib/branding';
import type { TenantSettings } from '@/lib/types';
import { readableForeground } from '@/lib/utils';

const TIMEZONES = Intl.supportedValuesOf('timeZone');
const SEQUENCE_LABELS: Record<string, string> = { customer: 'Kunden', ticket: 'Tickets', repair: 'Reparaturen', contract: 'Verträge' };

function useTenant() {
  return useQuery({ queryKey: ['tenant'], queryFn: () => api<TenantSettings>('/tenant') });
}

function ProfileForm({ tenant }: { tenant: TenantSettings }) {
  const qc = useQueryClient();
  const form = useForm<TenantProfileInput>({
    resolver: zodResolver(tenantProfileSchema),
    values: {
      name: tenant.name,
      legalName: tenant.legalName ?? '',
      email: tenant.email ?? '',
      phone: tenant.phone ?? '',
      website: tenant.website ?? '',
      street: tenant.street ?? '',
      postalCode: tenant.postalCode ?? '',
      city: tenant.city ?? '',
      country: tenant.country,
      vatId: tenant.vatId ?? '',
      timezone: tenant.timezone,
      primaryColor: tenant.primaryColor,
      accentColor: tenant.accentColor ?? undefined,
      portalSelfRegistration: tenant.portalSelfRegistration,
      repairSlipQr: tenant.repairSlipQr,
      repairPublicStatus: tenant.repairPublicStatus,
      ticketGuestAccess: tenant.ticketGuestAccess,
    },
  });
  const primary = form.watch('primaryColor');
  const mutation = useMutation({
    mutationFn: (v: TenantProfileInput) => api<TenantSettings>('/tenant', { method: 'PUT', body: v }),
    onSuccess: (data) => {
      qc.setQueryData(['tenant'], data);
      qc.invalidateQueries({ queryKey: ME_KEY });
      toast.success('Unternehmensdaten gespeichert');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const err = form.formState.errors;

  return (
    <form onSubmit={form.handleSubmit((v) => mutation.mutate(v))} noValidate className="flex flex-col gap-4">
      <Card>
        <CardHeader>
          <CardTitle>Unternehmensdaten</CardTitle>
          <CardDescription>Erscheinen in E-Mails und später auf Belegen.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <Field label="Anzeigename" htmlFor="t-name" error={err.name?.message}>
            <Input id="t-name" {...form.register('name')} />
          </Field>
          <Field label="Firmierung (rechtlich)" htmlFor="t-legal">
            <Input id="t-legal" {...form.register('legalName')} />
          </Field>
          <Field label="E-Mail" htmlFor="t-email" error={err.email?.message}>
            <Input id="t-email" type="email" {...form.register('email')} />
          </Field>
          <Field label="Telefon" htmlFor="t-phone">
            <Input id="t-phone" {...form.register('phone')} />
          </Field>
          <Field label="Straße und Hausnummer" htmlFor="t-street" className="sm:col-span-2">
            <Input id="t-street" {...form.register('street')} />
          </Field>
          <div className="grid grid-cols-3 gap-4 sm:col-span-2">
            <Field label="PLZ" htmlFor="t-zip">
              <Input id="t-zip" {...form.register('postalCode')} />
            </Field>
            <Field label="Ort" htmlFor="t-city" className="col-span-2">
              <Input id="t-city" {...form.register('city')} />
            </Field>
          </div>
          <Field label="Land (ISO)" htmlFor="t-country" error={err.country?.message}>
            <Input id="t-country" maxLength={2} {...form.register('country')} />
          </Field>
          <Field label="USt-IdNr." htmlFor="t-vat">
            <Input id="t-vat" {...form.register('vatId')} />
          </Field>
          <Field label="Website" htmlFor="t-web">
            <Input id="t-web" {...form.register('website')} />
          </Field>
          <Field label="Zeitzone" htmlFor="t-tz" hint="Grundlage für Termine, Fristen und Auswertungen">
            <Select id="t-tz" {...form.register('timezone')}>
              {TIMEZONES.map((tz) => (
                <option key={tz} value={tz}>
                  {tz}
                </option>
              ))}
            </Select>
          </Field>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Farben</CardTitle>
          <CardDescription>Die Primärfarbe prägt Schaltflächen, Navigation und Anmeldeseite.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <Field label="Primärfarbe" htmlFor="t-primary" error={err.primaryColor?.message}>
            <div className="flex gap-2">
              <input
                type="color"
                aria-label="Primärfarbe wählen"
                className="h-9 w-12 cursor-pointer rounded-md border bg-card p-1"
                value={/^#[0-9a-f]{6}$/i.test(primary ?? '') ? primary : '#000000'}
                onChange={(e) => form.setValue('primaryColor', e.target.value, { shouldDirty: true })}
              />
              <Input id="t-primary" {...form.register('primaryColor')} className="font-mono" />
            </div>
          </Field>
          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium">Vorschau</span>
            <div className="flex h-9 items-center gap-2">
              <span
                className="inline-flex h-9 items-center rounded-md px-4 text-sm font-medium shadow-sm"
                style={{ background: primary, color: readableForeground(primary ?? '#000000') }}
              >
                Schaltfläche
              </span>
              <span className="text-sm font-medium" style={{ color: primary }}>
                Link
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Kundenportal</CardTitle>
          <CardDescription>Welche Bereiche Kunden nutzen dürfen, steuern Sie in der Ticket-Konfiguration.</CardDescription>
        </CardHeader>
        <CardContent>
          <label className="flex items-start gap-3 text-sm">
            <Switch
              className="mt-0.5"
              checked={form.watch('portalSelfRegistration')}
              onCheckedChange={(v) => form.setValue('portalSelfRegistration', v, { shouldDirty: true })}
            />
            <span>
              <span className="block font-medium">Selbstregistrierung erlauben</span>
              <span className="text-muted-foreground">Kunden können sich auf der Anmeldeseite selbst ein Konto anlegen (mit E-Mail-Bestätigung).</span>
            </span>
          </label>
          <label className="mt-4 flex items-start gap-3 text-sm">
            <Switch className="mt-0.5" checked={form.watch('repairSlipQr')} onCheckedChange={(v) => form.setValue('repairSlipQr', v, { shouldDirty: true })} />
            <span>
              <span className="block font-medium">QR-Code auf dem Annahmeschein</span>
              <span className="text-muted-foreground">Kunden scannen den Code, um den Stand ihrer Reparatur zu sehen.</span>
            </span>
          </label>
          <label className="mt-4 flex items-start gap-3 text-sm">
            <Switch
              className="mt-0.5"
              checked={form.watch('repairPublicStatus')}
              onCheckedChange={(v) => form.setValue('repairPublicStatus', v, { shouldDirty: true })}
            />
            <span>
              <span className="block font-medium">Reparaturstatus ohne Anmeldung (mit PIN)</span>
              <span className="text-muted-foreground">
                Der QR-Code führt zu einer Statusseite; angezeigt wird sie nach Eingabe der PIN, die ebenfalls auf dem Annahmeschein steht. Ausgeschaltet führt der QR-Code ins Kundenportal (Anmeldung nötig).
              </span>
            </span>
          </label>
          <label className="mt-4 flex items-start gap-3 text-sm">
            <Switch className="mt-0.5" checked={form.watch('ticketGuestAccess')} onCheckedChange={(v) => form.setValue('ticketGuestAccess', v, { shouldDirty: true })} />
            <span>
              <span className="block font-medium">Tickets ohne Kundenkonto ansehen und beantworten</span>
              <span className="text-muted-foreground">
                Kunden ohne Portalzugang erhalten in Ticket-Mails einen Link. Geöffnet wird die Anfrage nach Eingabe eines Einmalcodes, der an die im Ticket hinterlegte E-Mail-Adresse geht.
              </span>
            </span>
          </label>
        </CardContent>
        <CardFooter>
          <Button type="submit" loading={mutation.isPending} disabled={!form.formState.isDirty}>
            Speichern
          </Button>
        </CardFooter>
      </Card>
    </form>
  );
}

function ImageUpload({ kind, tenant }: { kind: 'logo' | 'favicon'; tenant: TenantSettings }) {
  const qc = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const has = kind === 'logo' ? tenant.hasLogo : tenant.hasFavicon;
  const [version, setVersion] = useState(() => String(Date.now()));
  const onDone = (data: TenantSettings) => {
    qc.setQueryData(['tenant'], data);
    qc.invalidateQueries({ queryKey: ME_KEY });
    qc.invalidateQueries({ queryKey: ['public', 'tenant'] });
    setVersion(String(Date.now()));
  };
  const upload = useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append('file', file);
      return api<TenantSettings>(`/tenant/branding/${kind}`, { method: 'POST', form });
    },
    onSuccess: (d) => {
      onDone(d);
      toast.success(kind === 'logo' ? 'Logo hochgeladen' : 'Favicon hochgeladen');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const remove = useMutation({
    mutationFn: () => api<TenantSettings>(`/tenant/branding/${kind}`, { method: 'DELETE' }),
    onSuccess: onDone,
    onError: (e) => toast.error(errorMessage(e)),
  });
  const src = kind === 'logo' ? logoUrl(version) : `/api/public/tenant/favicon?v=${version}`;

  return (
    <div className="flex items-center gap-4">
      <div className="flex size-20 shrink-0 items-center justify-center rounded-lg border bg-muted/40 p-2">
        {has ? <img src={src} alt="" className="max-h-full max-w-full object-contain" /> : <ImageUp className="size-6 text-muted-foreground" />}
      </div>
      <div className="flex flex-col gap-2">
        <p className="text-sm font-medium">{kind === 'logo' ? 'Logo' : 'Favicon'}</p>
        <p className="text-xs text-muted-foreground">{kind === 'logo' ? 'PNG, JPEG oder WebP, max. 2 MB' : 'ICO oder PNG (quadratisch), max. 2 MB'}</p>
        <div className="flex gap-2">
          <input
            ref={input}
            type="file"
            accept={kind === 'logo' ? 'image/png,image/jpeg,image/webp' : 'image/png,image/x-icon,.ico'}
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) upload.mutate(f);
              e.target.value = '';
            }}
          />
          <Button size="sm" variant="outline" onClick={() => input.current?.click()} loading={upload.isPending}>
            {has ? 'Ersetzen' : 'Hochladen'}
          </Button>
          {has && (
            <Button size="sm" variant="ghost" onClick={() => remove.mutate()} loading={remove.isPending} aria-label="Entfernen">
              <Trash2 />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function SequenceRow({ seq }: { seq: TenantSettings['numberSequences'][number] }) {
  const qc = useQueryClient();
  const [prefix, setPrefix] = useState(seq.prefix);
  const [padding, setPadding] = useState(seq.padding);
  const [nextValue, setNextValue] = useState(seq.nextValue);
  useEffect(() => {
    setPrefix(seq.prefix);
    setPadding(seq.padding);
    setNextValue(seq.nextValue);
  }, [seq]);
  const dirty = prefix !== seq.prefix || padding !== seq.padding || nextValue !== seq.nextValue;
  const save = useMutation({
    mutationFn: () => api<TenantSettings>(`/tenant/number-sequences/${seq.key}`, { method: 'PUT', body: { prefix, padding, nextValue } }),
    onSuccess: (d) => {
      qc.setQueryData(['tenant'], d);
      toast.success('Nummernkreis gespeichert');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  return (
    <div className="grid grid-cols-2 items-end gap-3 border-b py-3 last:border-0 sm:grid-cols-[1fr_6rem_5rem_7rem_auto]">
      <div className="col-span-2 sm:col-span-1">
        <p className="text-sm font-medium">{SEQUENCE_LABELS[seq.key] ?? seq.key}</p>
        <p className="font-mono text-xs text-muted-foreground">
          Nächste: {prefix}
          {String(nextValue).padStart(padding, '0')}
        </p>
      </div>
      <Field label="Präfix">
        <Input value={prefix} onChange={(e) => setPrefix(e.target.value)} maxLength={12} />
      </Field>
      <Field label="Stellen">
        <Input type="number" min={1} max={10} value={padding} onChange={(e) => setPadding(Number(e.target.value))} />
      </Field>
      <Field label="Nächste Nr.">
        <Input type="number" min={seq.nextValue} value={nextValue} onChange={(e) => setNextValue(Number(e.target.value))} />
      </Field>
      <Button size="sm" variant="outline" disabled={!dirty} loading={save.isPending} onClick={() => save.mutate()}>
        Speichern
      </Button>
    </div>
  );
}

export function TenantPage() {
  const { data: tenant } = useTenant();
  if (!tenant) return <Skeleton className="h-96" />;
  return (
    <>
      <PageHeader title="Unternehmen" description="Stammdaten, Branding, Domains und Nummernkreise" />
      <div className="grid gap-4 xl:grid-cols-3">
        <div className="xl:col-span-2">
          <ProfileForm tenant={tenant} />
        </div>
        <div className="flex flex-col gap-4">
          <Card>
            <CardHeader>
              <CardTitle>Logo & Favicon</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-5">
              <ImageUpload kind="logo" tenant={tenant} />
              <ImageUpload kind="favicon" tenant={tenant} />
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>Domains</CardTitle>
              <CardDescription>Werden vom Plattformbetreiber eingerichtet (DNS und Zertifikat).</CardDescription>
            </CardHeader>
            <CardContent>
              {tenant.domains.length === 0 ? (
                <p className="text-sm text-muted-foreground">Keine eigene Domain hinterlegt.</p>
              ) : (
                <ul className="flex flex-col gap-2">
                  {tenant.domains.map((d) => (
                    <li key={d.id} className="flex items-center gap-2 text-sm">
                      <Globe className="size-4 text-muted-foreground" />
                      <span className="font-mono">{d.domain}</span>
                      {d.isPrimary && <Badge>primär</Badge>}
                    </li>
                  ))}
                </ul>
              )}
              <p className="mt-3 text-xs text-muted-foreground">Kürzel: <span className="font-mono">{tenant.slug}</span></p>
            </CardContent>
          </Card>
        </div>
        <Card className="xl:col-span-3">
          <CardHeader>
            <CardTitle>Nummernkreise</CardTitle>
            <CardDescription>Vergabe erfolgt fortlaufend und eindeutig. Die nächste Nummer kann nur erhöht werden.</CardDescription>
          </CardHeader>
          <CardContent>
            {tenant.numberSequences.map((s) => (
              <SequenceRow key={s.key} seq={s} />
            ))}
          </CardContent>
        </Card>
      </div>
    </>
  );
}
